import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import equipmentService from '../../services/equipment';
import orchardService from '../../services/orchard';
import EquipmentTypePicker from '../../components/equipment/EquipmentTypePicker';
import EquipmentPhotos from '../../components/equipment/EquipmentPhotos';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import type { Farm } from '../../types/orchard';
import { EQUIPMENT_CATEGORIES, categoryOf, isRoadVehicleCategory } from '../../types/equipment';
import type {
  AuRegoState, FuelType, TransmissionType, DriveType, EquipmentCategory, EquipmentTypeOption,
} from '../../types/equipment';
import { cn } from '../../utils/cn';
import { EQUIPMENT_COLOURS } from '../../utils/equipmentColours';

interface FormState {
  name: string;
  equipment_type: string;
  custom_type_label: string;
  /** What kind of thing it is — decides which cards the form shows. */
  category: EquipmentCategory;
  make: string;
  model: string;
  serial_number: string;
  year_of_manufacture: string;
  location_name: string;
  farm_id: string;
  notes: string;
  meter_type: 'hours' | 'km' | 'both' | 'none';
  current_hours: string;
  current_km: string;
  can_tow_implements: boolean;
  is_implement: boolean;
  status: 'active' | 'inactive' | 'under_repair' | 'retired';
  working_width_m: string;
  // Spray calibration
  tank_capacity_litres: string;
  nozzle_type: string;
  nozzle_spacing_cm: string;
  boom_width_m: string;
  output_rate_lpha: string;
  calibration_date: string;
  // Road-vehicle fields
  vin: string;
  rego_number: string;
  rego_state: AuRegoState | '';
  rego_expiry: string;
  engine_number: string;
  engine_code: string;
  engine_capacity_l: string;
  fuel_type: FuelType | '';
  transmission: TransmissionType | '';
  drive_type: DriveType | '';
  body_type: string;
  colour: string;
}

const INITIAL: FormState = {
  name: '', equipment_type: '', custom_type_label: '', category: 'other', make: '', model: '',
  serial_number: '', year_of_manufacture: '', location_name: '', farm_id: '',
  notes: '', meter_type: 'none', current_hours: '', current_km: '',
  can_tow_implements: false, is_implement: false, status: 'active',
  working_width_m: '',
  tank_capacity_litres: '', nozzle_type: '', nozzle_spacing_cm: '', boom_width_m: '', output_rate_lpha: '', calibration_date: '',
  vin: '', rego_number: '', rego_state: '', rego_expiry: '',
  engine_number: '', engine_code: '', engine_capacity_l: '',
  fuel_type: '', transmission: '', drive_type: '', body_type: '', colour: '',
};

const REGO_STATES: AuRegoState[] = ['NSW','VIC','QLD','SA','WA','TAS','NT','ACT'];

export default function EquipmentForm() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm]       = useState<FormState>(INITIAL);
  const [farms, setFarms]     = useState<Farm[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [trackAsRoadVehicle, setTrackAsRoadVehicle] = useState(false);
  // Photos picked before a new machine exists; uploaded once it has an id.
  const [stagedPhotos, setStagedPhotos] = useState<File[]>([]);
  const { hasAccess: canEditPhotos } = useModuleLicense('vehicles', ['creator', 'admin']);

  useEffect(() => {
    orchardService.getFarms(1, 100).then(res => {
      if (res.success && res.data) setFarms(res.data.farms);
    });

    if (isEdit) {
      equipmentService.getById(parseInt(id!)).then(res => {
        if (res.success && res.data) {
          const eq = res.data;
          setForm({
            name: eq.name,
            equipment_type: eq.equipment_type,
            custom_type_label: eq.custom_type_label ?? '',
            category: categoryOf(eq),
            make: eq.make ?? '',
            model: eq.model ?? '',
            serial_number: eq.serial_number ?? '',
            year_of_manufacture: eq.year_of_manufacture?.toString() ?? '',
            location_name: eq.location_name ?? '',
            farm_id: eq.farm_id?.toString() ?? '',
            notes: eq.notes ?? '',
            meter_type: eq.meter_type,
            current_hours: eq.current_hours?.toString() ?? '',
            current_km: eq.current_km?.toString() ?? '',
            can_tow_implements: eq.can_tow_implements,
            is_implement: eq.is_implement,
            status: eq.status,
            working_width_m: eq.working_width_m?.toString() ?? '',
            tank_capacity_litres: eq.tank_capacity_litres?.toString() ?? '',
            nozzle_type: eq.nozzle_type ?? '',
            nozzle_spacing_cm: eq.nozzle_spacing_cm?.toString() ?? '',
            boom_width_m: eq.boom_width_m?.toString() ?? '',
            output_rate_lpha: eq.output_rate_lpha?.toString() ?? '',
            calibration_date: eq.calibration_date ?? '',
            vin: eq.vin ?? '',
            rego_number: eq.rego_number ?? '',
            rego_state: (eq.rego_state ?? '') as AuRegoState | '',
            rego_expiry: eq.rego_expiry ?? '',
            engine_number: eq.engine_number ?? '',
            engine_code: eq.engine_code ?? '',
            engine_capacity_l: eq.engine_capacity_l?.toString() ?? '',
            fuel_type: (eq.fuel_type ?? '') as FuelType | '',
            transmission: (eq.transmission ?? '') as TransmissionType | '',
            drive_type: (eq.drive_type ?? '') as DriveType | '',
            body_type: eq.body_type ?? '',
            colour: eq.colour ?? '',
          });
          // Auto-open the road-vehicle card if any of its fields have values.
          if (eq.vin || eq.rego_number || eq.rego_expiry || eq.engine_code || eq.fuel_type) {
            setTrackAsRoadVehicle(true);
          }
        }
      }).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  // A picked type brings its category and the defaults a machine of that type
  // starts with (from the registry, so a custom type's are honoured too). They
  // are only a starting point — every one stays editable below. A brand-new
  // custom type has no registry row yet, so it keeps whatever is set and asks
  // what kind of thing it is.
  function handleTypeChange(type: string, customLabel: string, option?: EquipmentTypeOption) {
    setForm(f => ({
      ...f,
      equipment_type: type,
      custom_type_label: customLabel,
      category: option ? categoryOf({ equipment_type: type, equipment_category: option.category }) : f.category,
      ...(option && option.default_is_implement !== undefined ? {
        is_implement:       option.default_is_implement,
        can_tow_implements: option.default_can_tow ?? false,
        meter_type:         option.default_meter_type ?? f.meter_type,
      } : {}),
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      name:               form.name,
      equipment_type:     form.equipment_type,
      custom_type_label:  form.custom_type_label || null,
      custom_type_category: form.equipment_type === 'custom' ? form.category : null,
      make:               form.make || null,
      model:              form.model || null,
      serial_number:      form.serial_number || null,
      year_of_manufacture: form.year_of_manufacture ? parseInt(form.year_of_manufacture) : null,
      location_name:      form.location_name || null,
      farm_id:            form.farm_id ? parseInt(form.farm_id) : null,
      notes:              form.notes || null,
      meter_type:         form.meter_type,
      current_hours:      form.current_hours ? parseFloat(form.current_hours) : null,
      current_km:         form.current_km    ? parseFloat(form.current_km)    : null,
      can_tow_implements: form.can_tow_implements,
      is_implement:       form.is_implement,
      working_width_m:    form.working_width_m ? parseFloat(form.working_width_m) : null,
      status:             form.status,
      tank_capacity_litres: form.tank_capacity_litres ? parseFloat(form.tank_capacity_litres) : null,
      nozzle_type:        form.nozzle_type || null,
      output_rate_lpha:   form.output_rate_lpha ? parseFloat(form.output_rate_lpha) : null,
      calibration_date:   form.calibration_date || null,
      vin:                form.vin ? form.vin.trim().toUpperCase() : null,
      rego_number:        form.rego_number ? form.rego_number.trim().toUpperCase() : null,
      rego_state:         form.rego_state || null,
      rego_expiry:        form.rego_expiry || null,
      engine_number:      form.engine_number || null,
      engine_code:        form.engine_code || null,
      engine_capacity_l:  form.engine_capacity_l ? parseFloat(form.engine_capacity_l) : null,
      fuel_type:          form.fuel_type || null,
      transmission:       form.transmission || null,
      drive_type:         form.drive_type || null,
      body_type:          form.body_type || null,
      colour:             form.colour || null,
    };

    try {
      const res = isEdit
        ? await equipmentService.update(parseInt(id!), payload)
        : await equipmentService.create(payload);

      if (res.success && res.data) {
        const newId = res.data.id;
        // The machine is saved either way; a photo that fails here can be
        // added again from its page, so it must not hold up the navigation.
        for (const [i, file] of stagedPhotos.entries()) {
          try {
            await equipmentService.uploadPhoto(newId, file, { is_primary: i === 0 });
          } catch { /* retried by hand from the detail page */ }
        }
        navigate(`/equipment/${newId}`);
      } else {
        setError('Failed to save equipment.');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const showSprayFields = form.category === 'sprayer';
  // Auto-reveal for vehicles and machinery; otherwise the user can toggle it
  // on manually. This lets road-registered items (utes, trucks) capture
  // VIN/rego without forcing every implement or hand tool to show the card.
  const showRoadFields  = trackAsRoadVehicle || isRoadVehicleCategory(form.category);
  const vinValid = !form.vin || /^[A-HJ-NPR-Z0-9]{17}$/.test(form.vin.trim().toUpperCase());

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PageHeader title={isEdit ? 'Edit Equipment' : 'Add Equipment'} />
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="h-96 bg-white rounded-xl animate-pulse border border-neutral-100" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <PageHeader title={isEdit ? `Edit ${form.name || 'Equipment'}` : 'Add Equipment'} />

      <div className="max-w-2xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-5">

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">{error}</div>
          )}

          <Card>
            <CardContent className="pt-4 space-y-4">
              <h3 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Basic Info</h3>

              <Field label="Name *">
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  required placeholder="e.g. John Deere 6140M"
                  className={inputClass} />
              </Field>

              <Field label="Type *">
                <EquipmentTypePicker
                  value={form.equipment_type}
                  customLabel={form.custom_type_label}
                  onChange={handleTypeChange}
                />
              </Field>

              {form.equipment_type === 'custom' && (
                <Field label="What kind of equipment is it?">
                  <select
                    value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value as EquipmentCategory }))}
                    className={inputClass}
                  >
                    {EQUIPMENT_CATEGORIES.map(c => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-neutral-400 mt-1">
                    Decides where &ldquo;{form.custom_type_label}&rdquo; shows up — a sprayer gets a calibration
                    card, a pump can be linked to an irrigation relay for its hours.
                  </p>
                </Field>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Field label="Make">
                  <input value={form.make} onChange={e => setForm(f => ({ ...f, make: e.target.value }))}
                    placeholder="e.g. John Deere" className={inputClass} />
                </Field>
                <Field label="Model">
                  <input value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))}
                    placeholder="e.g. 6140M" className={inputClass} />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Serial Number">
                  <input value={form.serial_number} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))}
                    className={inputClass} />
                </Field>
                <Field label="Year">
                  <input type="number" value={form.year_of_manufacture}
                    onChange={e => setForm(f => ({ ...f, year_of_manufacture: e.target.value }))}
                    min={1900} max={2100} className={inputClass} />
                </Field>
              </div>

              <Field label="Colour">
                <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Colour">
                  {EQUIPMENT_COLOURS.map(c => {
                    const selected = form.colour === c.name;
                    return (
                      <button
                        key={c.name}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        // Tapping the chosen colour again clears it.
                        onClick={() => setForm(f => ({ ...f, colour: selected ? '' : c.name }))}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs',
                          selected
                            ? 'border-green-600 bg-green-50 text-green-800 ring-1 ring-green-600'
                            : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50',
                        )}
                      >
                        <span className="h-3 w-3 rounded-full border border-neutral-300" style={{ backgroundColor: c.hex }} />
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field label="Farm / Location">
                <select
                  value={form.farm_id}
                  onChange={e => setForm(f => ({ ...f, farm_id: e.target.value }))}
                  className={inputClass}
                >
                  <option value="">— No farm assigned —</option>
                  {farms.map(f => (
                    <option key={f.id} value={f.id}>{f.name}{f.location ? ` (${f.location})` : ''}</option>
                  ))}
                </select>
              </Field>

              <Field label="Storage Location">
                <input value={form.location_name} onChange={e => setForm(f => ({ ...f, location_name: e.target.value }))}
                  placeholder="e.g. Machinery Shed A" className={inputClass} />
              </Field>
            </CardContent>
          </Card>

          {/* Meter tracking */}
          <Card>
            <CardContent className="pt-4 space-y-4">
              <h3 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Meter Tracking</h3>
              <p className="text-xs text-neutral-400">Set the current meter reading. This will be updated each time a session is completed.</p>

              <Field label="Track by">
                <select value={form.meter_type} onChange={e => setForm(f => ({ ...f, meter_type: e.target.value as FormState['meter_type'] }))}
                  className={inputClass}>
                  <option value="none">None</option>
                  <option value="hours">Hours only</option>
                  <option value="km">Kilometres only</option>
                  <option value="both">Both hours & km</option>
                </select>
              </Field>

              {(form.meter_type === 'hours' || form.meter_type === 'both') && (
                <Field label="Current Hours">
                  <input type="number" step="0.1" min="0" value={form.current_hours}
                    onChange={e => setForm(f => ({ ...f, current_hours: e.target.value }))}
                    placeholder="0.0" className={inputClass} />
                </Field>
              )}

              {(form.meter_type === 'km' || form.meter_type === 'both') && (
                <Field label="Current Km">
                  <input type="number" step="1" min="0" value={form.current_km}
                    onChange={e => setForm(f => ({ ...f, current_km: e.target.value }))}
                    placeholder="0" className={inputClass} />
                </Field>
              )}
            </CardContent>
          </Card>

          {/* Implement settings */}
          <Card>
            <CardContent className="pt-4 space-y-3">
              <h3 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Implement Settings</h3>

              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.can_tow_implements}
                  onChange={e => setForm(f => ({ ...f, can_tow_implements: e.target.checked }))}
                  className="h-4 w-4 rounded text-green-600 border-neutral-300" />
                <span className="text-sm text-neutral-700">Can Tow/Use Implements</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.is_implement}
                  onChange={e => setForm(f => ({ ...f, is_implement: e.target.checked }))}
                  className="h-4 w-4 rounded text-green-600 border-neutral-300" />
                <span className="text-sm text-neutral-700">This is an implement (can be attached to a tractor or other equipment)</span>
              </label>

              <div className="mt-3">
                <label className="block text-sm font-medium text-neutral-700 mb-1">Working Width (m)</label>
                <input type="number" step="0.1" min="0" value={form.working_width_m || ''}
                  onChange={e => setForm(f => ({ ...f, working_width_m: e.target.value }))}
                  placeholder="e.g. 3.0 for a 3m slasher"
                  className="w-full sm:w-1/2 border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                <p className="text-xs text-neutral-400 mt-1">Used for GPS coverage calculation in farm jobs. Leave blank if not applicable.</p>
              </div>
            </CardContent>
          </Card>

          {/* Road-vehicle toggle (always visible) */}
          <Card>
            <CardContent className="pt-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showRoadFields}
                  onChange={e => setTrackAsRoadVehicle(e.target.checked)}
                  className="h-4 w-4 mt-0.5 rounded text-green-600 border-neutral-300"
                />
                <div>
                  <span className="text-sm font-medium text-neutral-800">Track as road-registered vehicle</span>
                  <p className="text-xs text-neutral-400">Captures VIN, rego number and expiry. Enable for anything driven on public roads (ute, truck, car, van).</p>
                </div>
              </label>
            </CardContent>
          </Card>

          {/* Road vehicle details */}
          {showRoadFields && (
            <Card>
              <CardContent className="pt-4 space-y-4">
                <h3 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Registration & Identification</h3>
                <p className="text-xs text-neutral-400">VIN, rego and engine info. Use the <code>vehicle-parts-lookup</code> skill to auto-populate filters and oils once saved.</p>

                <Field label="VIN (17-char)">
                  <input
                    value={form.vin}
                    onChange={e => setForm(f => ({ ...f, vin: e.target.value.toUpperCase() }))}
                    maxLength={17}
                    placeholder="e.g. JTEBU5JR0L5801234"
                    className={cn(inputClass, 'font-mono tracking-wider', !vinValid && 'border-red-400')}
                  />
                  {!vinValid && (
                    <p className="text-xs text-red-600 mt-1">VIN must be 17 characters — letters (no I, O, Q) and digits only.</p>
                  )}
                </Field>

                <div className="grid grid-cols-3 gap-3">
                  <Field label="Rego Number">
                    <input value={form.rego_number}
                      onChange={e => setForm(f => ({ ...f, rego_number: e.target.value.toUpperCase() }))}
                      maxLength={10}
                      placeholder="e.g. ABC123" className={cn(inputClass, 'uppercase')} />
                  </Field>
                  <Field label="State">
                    <select value={form.rego_state}
                      onChange={e => setForm(f => ({ ...f, rego_state: e.target.value as AuRegoState | '' }))}
                      className={inputClass}>
                      <option value="">—</option>
                      {REGO_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Rego Expiry">
                    <input type="date" value={form.rego_expiry}
                      onChange={e => setForm(f => ({ ...f, rego_expiry: e.target.value }))}
                      className={inputClass} />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Engine Number">
                    <input value={form.engine_number}
                      onChange={e => setForm(f => ({ ...f, engine_number: e.target.value }))}
                      className={inputClass} />
                  </Field>
                  <Field label="Engine Code">
                    <input value={form.engine_code}
                      onChange={e => setForm(f => ({ ...f, engine_code: e.target.value }))}
                      placeholder="e.g. 1GD-FTV" className={inputClass} />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Capacity (L)">
                    <input type="number" step="0.1" min="0" value={form.engine_capacity_l}
                      onChange={e => setForm(f => ({ ...f, engine_capacity_l: e.target.value }))}
                      placeholder="e.g. 2.8" className={inputClass} />
                  </Field>
                  <Field label="Body Type">
                    <input value={form.body_type}
                      onChange={e => setForm(f => ({ ...f, body_type: e.target.value }))}
                      placeholder="e.g. Dual cab" className={inputClass} />
                  </Field>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <Field label="Fuel">
                    <select value={form.fuel_type}
                      onChange={e => setForm(f => ({ ...f, fuel_type: e.target.value as FuelType | '' }))}
                      className={inputClass}>
                      <option value="">—</option>
                      <option value="diesel">Diesel</option>
                      <option value="petrol">Petrol</option>
                      <option value="electric">Electric</option>
                      <option value="hybrid">Hybrid</option>
                      <option value="lpg">LPG</option>
                    </select>
                  </Field>
                  <Field label="Transmission">
                    <select value={form.transmission}
                      onChange={e => setForm(f => ({ ...f, transmission: e.target.value as TransmissionType | '' }))}
                      className={inputClass}>
                      <option value="">—</option>
                      <option value="manual">Manual</option>
                      <option value="auto">Auto</option>
                      <option value="cvt">CVT</option>
                      <option value="dct">DCT</option>
                    </select>
                  </Field>
                  <Field label="Drive">
                    <select value={form.drive_type}
                      onChange={e => setForm(f => ({ ...f, drive_type: e.target.value as DriveType | '' }))}
                      className={inputClass}>
                      <option value="">—</option>
                      <option value="2wd">2WD</option>
                      <option value="4wd">4WD</option>
                      <option value="awd">AWD</option>
                    </select>
                  </Field>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sprayer details */}
          {showSprayFields && (
            <Card>
              <CardContent className="pt-4 space-y-4">
                <h3 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Sprayer Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Tank Capacity (L)">
                    <input type="number" step="0.1" min="0" value={form.tank_capacity_litres}
                      onChange={e => setForm(f => ({ ...f, tank_capacity_litres: e.target.value }))}
                      className={inputClass} />
                  </Field>
                  <Field label="Nozzle Spacing (cm)">
                    <input type="number" step="0.1" min="0" value={form.nozzle_spacing_cm || ''}
                      onChange={e => setForm(f => ({ ...f, nozzle_spacing_cm: e.target.value }))}
                      className={inputClass} />
                  </Field>
                  <Field label="Boom Width (m)">
                    <input type="number" step="0.01" min="0" value={form.boom_width_m || ''}
                      onChange={e => setForm(f => ({ ...f, boom_width_m: e.target.value }))}
                      className={inputClass} />
                  </Field>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Status (edit only) */}
          {isEdit && (
            <Card>
              <CardContent className="pt-4 space-y-4">
                <h3 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Status</h3>
                <Field label="Equipment Status">
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as FormState['status'] }))}
                    className={inputClass}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="under_repair">Under Repair</option>
                    <option value="retired">Retired</option>
                  </select>
                </Field>
                {form.status === 'retired' && (
                  <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    Retired equipment will not appear in active equipment lists or be available for new jobs.
                    Change status to Active to restore it.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Photos */}
          {(canEditPhotos || isEdit) && (
            <Card>
              <CardContent className="pt-4 space-y-3">
                <h3 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Photos</h3>
                <EquipmentPhotos
                  equipmentId={isEdit ? parseInt(id!) : undefined}
                  canEdit={canEditPhotos}
                  staged={stagedPhotos}
                  onStagedChange={setStagedPhotos}
                />
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          <Card>
            <CardContent className="pt-4">
              <Field label="Notes">
                <textarea rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className={cn(inputClass, 'resize-none')} placeholder="Any additional notes…" />
              </Field>
            </CardContent>
          </Card>

          <div className="flex gap-3 pb-8">
            <Button type="button" variant="outline" onClick={() => navigate(-1)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" isLoading={saving} disabled={!form.name || !form.equipment_type || !vinValid} className="flex-1">
              {isEdit ? 'Save Changes' : 'Create Equipment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const inputClass = 'w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm text-neutral-800 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-neutral-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
